import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Paper, Stack, Grid, Card, Avatar, Modal, IconButton, Zoom, Slide, Alert, Snackbar, Stepper, Step, StepLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import TextField from '@mui/material/TextField';
import StarIcon from '@mui/icons-material/Star';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Fade } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import { submitForm } from '../../utils/formSubmission';

const brandBlue = '#0B60B1';

const steps = [
  {
    label: 'Comprehensive Analysis',
    icon: <SearchIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We analyze your email infrastructure, authentication, and sending patterns to identify potential issues.'
  },
  {
    label: 'Detailed Report',
    icon: <ListAltIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'Receive a comprehensive report with actionable insights and recommendations.'
  },
  {
    label: 'Implementation Support',
    icon: <BuildIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'Get expert guidance on implementing the recommended changes and improvements.'
  },
  {
    label: 'Ongoing Monitoring',
    icon: <SpeedIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'Continuous monitoring of your email infrastructure to ensure optimal performance.'
  }
];

const testimonials = [
  {
    name: 'Alex M.',
    company: 'TechCorp',
    quote: 'The audit revealed critical issues we weren\'t aware of. Their recommendations were spot on!',
    avatar: '/images/testimonial1.jpg',
    stars: 5
  },
  {
    name: 'Rachel K.',
    company: 'DataFlow',
    quote: 'Most comprehensive email audit we\'ve ever had. Worth every penny!',
    avatar: '/images/testimonial2.jpg',
    stars: 5
  },
  {
    name: 'Tom B.',
    company: 'CloudScale',
    quote: 'They found issues that other auditors missed. Highly recommended!',
    avatar: '/images/testimonial3.jpg',
    stars: 5
  },
  {
    name: 'Sarah W.',
    company: 'MarketPro',
    quote: 'The detailed report helped us fix our email infrastructure completely.',
    avatar: '/images/testimonial4.jpg',
    stars: 5
  },
  {
    name: 'David L.',
    company: 'FinTechMail',
    quote: 'Their audit process is thorough and their recommendations are actionable.',
    avatar: '/images/testimonial5.jpg',
    stars: 5
  },
  {
    name: 'Lisa P.',
    company: 'EduReach',
    quote: 'The ongoing monitoring service has been invaluable for our email campaigns.',
    avatar: '/images/testimonial6.jpg',
    stars: 5
  }
];

const clientLogos = [
  '/images/client1.png',
  '/images/client2.png',
  '/images/client3.png',
  '/images/client4.png'
];

const faqs = [
  {
    q: 'What is included in an email audit?',
    a: 'Our comprehensive audit includes analysis of your email infrastructure, authentication setup, sending patterns, domain reputation, and technical configuration. We provide detailed recommendations for improvement.'
  },
  {
    q: 'How long does an audit take?',
    a: 'A complete email audit typically takes 2-3 business days, depending on the complexity of your setup and the volume of data to analyze.'
  },
  {
    q: 'Do you offer ongoing monitoring?',
    a: 'Yes, we offer continuous monitoring services to ensure your email infrastructure remains optimized and secure.'
  },
  {
    q: 'What makes your audit different?',
    a: 'Our audit combines technical analysis with industry best practices, providing actionable insights and implementation support to improve your email deliverability.'
  }
];

const services = [
  'Infrastructure Analysis',
  'Authentication Review',
  'Domain Reputation Check',
  'Sending Pattern Analysis',
  'Technical Configuration Audit',
  'Security Assessment',
  'Performance Optimization',
  'Best Practices Review',
  'Implementation Support',
  'Ongoing Monitoring'
];

const auditFactors = [
  {
    icon: <VerifiedUserIcon color="success" sx={{ fontSize: 36 }} />,
    title: 'Authentication Setup',
    desc: 'Review and optimize SPF, DKIM, and DMARC configurations.'
  },
  {
    icon: <ReportGmailerrorredIcon color="error" sx={{ fontSize: 36 }} />,
    title: 'Domain Reputation',
    desc: 'Analyze domain health and identify reputation issues.'
  },
  {
    icon: <MarkEmailReadIcon color="primary" sx={{ fontSize: 36 }} />,
    title: 'Sending Patterns',
    desc: 'Review email volume, frequency, and engagement metrics.'
  },
  {
    icon: <ListAltIcon color="secondary" sx={{ fontSize: 36 }} />,
    title: 'Technical Infrastructure',
    desc: 'Assess server configuration and email routing setup.'
  },
  {
    icon: <SpeedIcon color="info" sx={{ fontSize: 36 }} />,
    title: 'Performance Metrics',
    desc: 'Analyze delivery rates, bounces, and engagement data.'
  }
];

export default function EmailAuditingPage() {
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  // Help modal form state
  const [helpFormData, setHelpFormData] = useState({
    email: '',
    message: ''
  });
  const [isHelpSubmitting, setIsHelpSubmitting] = useState(false);
  
  // Audit request form state
  const [auditFormData, setAuditFormData] = useState({
    name: '',
    email: '',
    domains: '',
    auditType: 'Full',
    message: ''
  });
  const [isAuditSubmitting, setIsAuditSubmitting] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused) {
        setTestimonialIdx((prev) => (prev + 1) % Math.ceil(testimonials.length / 3));
      }
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isPaused]);

  const handleHelpInputChange = (field: string, value: string) => {
    setHelpFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAuditInputChange = (field: string, value: string) => {
    setAuditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleHelpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!helpFormData.email.trim() || !helpFormData.message.trim()) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(helpFormData.email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    setIsHelpSubmitting(true);

    try {
      const submissionData = {
        email: helpFormData.email,
        message: helpFormData.message,
        page_name: 'email_audit',
        request_for: 'Help Request',
        source: 'email_audit_help_modal',
        timestamp: new Date().toISOString()
      };

      await submitForm(submissionData, {
        formName: 'email_audit_help',
        formSource: 'email_audit_help_modal',
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: 'Thank you! We\'ll get back to you soon.',
            severity: 'success'
          });
          // Reset form and close modal
          setHelpFormData({ email: '', message: '' });
          setHelpOpen(false);
        },
        onError: (error) => {
          console.error('Help form submission error:', error);
          setSnackbar({
            open: true,
            message: 'Failed to send. Please try again.',
            severity: 'error'
          });
        },
        onLoading: (loading) => {
          setIsHelpSubmitting(loading);
        }
      });
      
    } catch (error) {
      console.error('Help form submission error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsHelpSubmitting(false);
    }
  };

  const handleAuditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!auditFormData.name.trim() || !auditFormData.email.trim()) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(auditFormData.email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    setIsAuditSubmitting(true);

    try {
      const submissionData = {
        name: auditFormData.name,
        email: auditFormData.email,
        domains: auditFormData.domains,
        audit_type: auditFormData.auditType,
        message: auditFormData.message,
        page_name: 'email_audit',
        request_for: 'Email Audit Request',
        source: 'email_audit_page',
        timestamp: new Date().toISOString()
      };

      await submitForm(submissionData, {
        formName: 'email_audit_request',
        formSource: 'email_audit_page',
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: 'Thank you! We\'ll contact you soon about your audit request.',
            severity: 'success'
          });
          // Reset form
          setAuditFormData({
            name: '',
            email: '',
            domains: '',
            auditType: 'Full',
            message: ''
          });
        },
        onError: (error) => {
          console.error('Audit form submission error:', error);
          setSnackbar({
            open: true,
            message: 'Failed to submit. Please try again.',
            severity: 'error'
          });
        },
        onLoading: (loading) => {
          setIsAuditSubmitting(loading);
        }
      });
      
    } catch (error) {
      console.error('Audit form submission error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsAuditSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', position: 'relative' }}>
      {/* Sticky Get Help Button */}
      <Zoom in>
        <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 2000 }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<HelpOutlineIcon />}
            sx={{ borderRadius: '50%', minWidth: 64, minHeight: 64, boxShadow: 6, fontWeight: 700, fontSize: 18, p: 0, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.08)' } }}
            onClick={() => setHelpOpen(true)}
            aria-label="Get Help"
          >
            Help
          </Button>
        </Box>
      </Zoom>

      {/* Help Modal */}
      <Modal open={helpOpen} onClose={() => setHelpOpen(false)}>
        <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: '#fff', p: 4, borderRadius: 3, boxShadow: 24, minWidth: 320, maxWidth: 400 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>How can we help?</Typography>
            <IconButton onClick={() => setHelpOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onSubmit={handleHelpSubmit}>
            <TextField 
              label="Your Email" 
              variant="outlined" 
              size="small" 
              fullWidth 
              required 
              type="email" 
              value={helpFormData.email}
              onChange={(e) => handleHelpInputChange('email', e.target.value)}
              disabled={isHelpSubmitting}
            />
            <TextField 
              label="How can we help?" 
              variant="outlined" 
              size="small" 
              fullWidth 
              multiline 
              minRows={3} 
              value={helpFormData.message}
              onChange={(e) => handleHelpInputChange('message', e.target.value)}
              disabled={isHelpSubmitting}
            />
            <Button 
              variant="contained" 
              color="primary" 
              type="submit" 
              sx={{ fontWeight: 700, mt: 1 }}
              disabled={isHelpSubmitting}
            >
              {isHelpSubmitting ? 'Sending...' : 'Send'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Hero Section with Animated SVG */}
      <Box sx={{ position: 'relative', bgcolor: brandBlue, color: '#fff', py: { xs: 8, md: 12 }, overflow: 'hidden' }}>
        {/* Animated SVG background */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          <svg width="100%" height="100%" viewBox="0 0 1440 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0 }}>
            <path fill="#1976d2" fillOpacity="0.18" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,133.3C840,107,960,85,1080,101.3C1200,117,1320,171,1380,197.3L1440,224L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z" />
          </svg>
        </Box>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Slide direction="down" in mountOnEnter unmountOnExit>
                <Box>
                  <Typography variant="h2" fontWeight={800} gutterBottom sx={{ letterSpacing: '-1px', mb: 2 }}>
                    Expert Email Auditing. <br /> Optimize Your Infrastructure.
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 400, opacity: 0.95, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIcon sx={{ fontSize: 32, mr: 1 }} /> Need to optimize your email setup? <b>We find what others miss.</b>
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button variant="contained" color="secondary" size="large" sx={{ fontWeight: 700, px: 4, boxShadow: 3, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>
                      Get Audit Quote
                    </Button>
                    <Button variant="outlined" color="inherit" size="large" sx={{ fontWeight: 700, borderColor: '#fff', px: 4, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>
                      View Sample Report
                    </Button>
                  </Stack>
                </Box>
              </Slide>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'relative', pt: { xs: 4, md: 0 }, width: '100%', maxWidth: 400, mx: 'auto' }}>
                <Paper elevation={6} sx={{ borderRadius: 4, p: 3, bgcolor: '#fff', minHeight: 320, boxShadow: '0 8px 32px rgba(11,96,177,0.10)' }}>
                  <Typography variant="h6" fontWeight={700} color="primary" sx={{ mb: 2 }}>
                    Audit Dashboard
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Issues Found</Typography>
                      <Typography variant="h5" fontWeight={700} color="error.main">12</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Critical</Typography>
                      <Typography variant="h5" fontWeight={700} color="error.main">3</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Recommendations</Typography>
                      <Typography variant="h5" fontWeight={700} color="success.main">15</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ bgcolor: '#f7fafd', borderRadius: 2, p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main">Key Findings</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">SPF Configuration</Typography>
                        <Typography variant="body2" color="error.main">Needs Fix</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">DKIM Setup</Typography>
                        <Typography variant="body2" color="success.main">Good</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">DMARC Policy</Typography>
                        <Typography variant="body2" color="warning.main">Warning</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main">Priority Actions</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                      <Typography variant="body2" color="error.main">• Update SPF records</Typography>
                      <Typography variant="body2" color="warning.main">• Configure DMARC</Typography>
                      <Typography variant="body2" color="success.main">• DKIM verified</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button variant="contained" color="primary" size="small" sx={{ fontWeight: 700, borderRadius: 2 }}>
                      View Full Report
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
        {/* Trusted by bar */}
        <Box sx={{ mt: 6, mb: -4, position: 'relative', zIndex: 2 }}>
          <Container maxWidth="md">
            <Paper elevation={2} sx={{ p: 2, borderRadius: 3, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mr: 2 }}>
                Trusted by:
              </Typography>
              {clientLogos.map((logo, idx) => (
                <Box key={idx} component="img" src={logo} alt={`Client ${idx+1}`} sx={{ height: 32, mx: 1, objectFit: 'contain', opacity: 0.8 }} />
              ))}
            </Paper>
          </Container>
        </Box>
      </Box>

      {/* Features/Stepper Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="flex-start">
          <Grid item xs={12} md={7}>
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{ mb: 4 }}>
              Your Audit Journey
            </Typography>
            <Stepper orientation="vertical" activeStep={-1} sx={{ background: 'transparent' }}>
              {steps.map((step) => (
                <Step key={step.label} completed={false} active={false}>
                  <StepLabel icon={step.icon}>
                    <Typography variant="h6" fontWeight={700}>{step.label}</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{step.desc}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card elevation={4} sx={{ p: 4, borderRadius: 3, bgcolor: 'white', boxShadow: '0 8px 32px rgba(11,96,177,0.08)', maxWidth: { xs: '100%', md: 520 }, width: '100%', mx: 'auto' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom color="primary">Request an Audit</Typography>
              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onSubmit={handleAuditSubmit}>
                <TextField 
                  label="Name" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  required 
                  value={auditFormData.name}
                  onChange={(e) => handleAuditInputChange('name', e.target.value)}
                  disabled={isAuditSubmitting}
                />
                <TextField 
                  label="Business Email" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  required 
                  type="email" 
                  value={auditFormData.email}
                  onChange={(e) => handleAuditInputChange('email', e.target.value)}
                  disabled={isAuditSubmitting}
                />
                <TextField 
                  label="Domains to Audit (one per line)" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  multiline 
                  minRows={2} 
                  placeholder="example.com&#10;anotherdomain.com" 
                  value={auditFormData.domains}
                  onChange={(e) => handleAuditInputChange('domains', e.target.value)}
                  disabled={isAuditSubmitting}
                />
                <TextField 
                  label="Audit Type" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  select 
                  value={auditFormData.auditType}
                  onChange={(e) => handleAuditInputChange('auditType', e.target.value)}
                  disabled={isAuditSubmitting}
                >
                  <MenuItem value="Full">Full Audit</MenuItem>
                  <MenuItem value="Quick">Quick Check</MenuItem>
                  <MenuItem value="Security">Security Focus</MenuItem>
                </TextField>
                <TextField 
                  label="Message" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  multiline 
                  minRows={3} 
                  value={auditFormData.message}
                  onChange={(e) => handleAuditInputChange('message', e.target.value)}
                  disabled={isAuditSubmitting}
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  type="submit" 
                  sx={{ fontWeight: 700, mt: 1, boxShadow: 2 }}
                  disabled={isAuditSubmitting}
                >
                  {isAuditSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Testimonial Carousel */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h5" fontWeight={800} textAlign="center" gutterBottom color="primary">
          Success Stories
        </Typography>
        <Box 
          sx={{ 
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              '& .MuiButton-root': {
                opacity: 1
              }
            }
          }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <Box sx={{ display: 'flex', transition: 'transform 0.5s ease', transform: `translateX(-${testimonialIdx * 100}%)` }}>
            {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, groupIndex) => (
              <Box key={groupIndex} sx={{ display: 'flex', gap: 3, minWidth: '100%', px: 2 }}>
                {testimonials.slice(groupIndex * 3, (groupIndex + 1) * 3).map((testimonial, index) => (
                  <Card 
                    key={index}
                    elevation={3} 
                    sx={{ 
                      p: 4, 
                      borderRadius: 3, 
                      flex: 1,
                      bgcolor: '#fff', 
                      transition: 'transform 0.2s', 
                      '&:hover': { 
                        transform: 'scale(1.04)', 
                        boxShadow: 8 
                      } 
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Avatar src={testimonial.avatar} alt={testimonial.name} sx={{ width: 48, height: 48 }} />
                      <Box>
                        <Typography fontWeight={700}>{testimonial.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{testimonial.company}</Typography>
                        <Box>
                          {[...Array(testimonial.stars)].map((_, i) => (
                            <StarIcon key={i} fontSize="small" color="warning" />
                          ))}
                        </Box>
                      </Box>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      " {testimonial.quote} "
                    </Typography>
                  </Card>
                ))}
              </Box>
            ))}
          </Box>
          <Button 
            onClick={() => setTestimonialIdx((prev) => (prev - 1 + Math.ceil(testimonials.length / 3)) % Math.ceil(testimonials.length / 3))} 
            sx={{ 
              position: 'absolute', 
              left: 0, 
              top: '50%', 
              transform: 'translateY(-50%)',
              opacity: 0,
              transition: 'opacity 0.2s',
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
            }}
          >
            &#8592;
          </Button>
          <Button 
            onClick={() => setTestimonialIdx((prev) => (prev + 1) % Math.ceil(testimonials.length / 3))} 
            sx={{ 
              position: 'absolute', 
              right: 0, 
              top: '50%', 
              transform: 'translateY(-50%)',
              opacity: 0,
              transition: 'opacity 0.2s',
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
            }}
          >
            &#8594;
          </Button>
        </Box>
      </Container>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {[
            { label: 'Audits Completed', value: '2,000+' },
            { label: 'Issues Found', value: '15,000+' },
            { label: 'Client Satisfaction', value: '99%' },
            { label: 'Average Score', value: '92%' }
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Our Services
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Comprehensive email auditing to optimize your infrastructure
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 4 }}>
          {services.map((service, index) => (
            <Chip
              key={service}
              label={service}
              color={index % 2 === 0 ? 'primary' : 'secondary'}
              variant={index % 3 === 0 ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: 16, px: 2, py: 2, height: 'auto', minHeight: 40, borderRadius: 2, boxShadow: 1 }}
            />
          ))}
        </Box>
      </Container>

      {/* Audit Factors Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom color="primary">
          What We Audit
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Our comprehensive audit covers every aspect of your email infrastructure.
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {auditFactors.map((factor, idx) => (
            <Grid item xs={12} sm={6} md={4} key={factor.title}>
              <Fade in timeout={600 + idx * 200}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-6px) scale(1.04)', boxShadow: 6 } }}>
                  <Box sx={{ mb: 2 }}>{factor.icon}</Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>{factor.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{factor.desc}</Typography>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section (Accordion) */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Stack spacing={3}>
          {faqs.map((faq) => (
            <Accordion key={faq.q} TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: 8, textAlign: 'center', mt: 8, position: 'relative' }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <VerifiedUserIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Box sx={{ bgcolor: '#fff', color: 'primary.main', px: 3, py: 1, borderRadius: 99, fontWeight: 700, fontSize: 18, boxShadow: 2, mb: 2, display: 'inline-block' }}>
              100% Satisfaction Guarantee
            </Box>
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>Ready to Optimize Your Email Infrastructure?</Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'rgba(255,255,255,0.85)' }}>
            Let our experts audit and improve your email setup.
          </Typography>
          <Button variant="contained" color="secondary" size="large" sx={{ fontWeight: 700, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>Start Your Audit</Button>
        </Container>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 